import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState(() => {
    const storedTokens = localStorage.getItem('tokens');
    return storedTokens ? JSON.parse(storedTokens) : null;
  });

  useEffect(() => {
    if (tokens) {
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [tokens]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/profile/');
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login/', { email, password });
      const { user, tokens } = response.data;
      setUser(user);
      setTokens(tokens);
      localStorage.setItem('tokens', JSON.stringify(tokens));
      localStorage.setItem('user', JSON.stringify(user));
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  };

  const register = async (email, password, passwordConfirm, role, customerInviteToken) => {
    try {
      const body = {
        email,
        password,
        password_confirm: passwordConfirm,
        role,
      };
      if (customerInviteToken) {
        body.customer_invite_token = customerInviteToken;
      }
      const response = await api.post('/auth/register/', body);
      const { user, tokens } = response.data;
      setUser(user);
      setTokens(tokens);
      localStorage.setItem('tokens', JSON.stringify(tokens));
      localStorage.setItem('user', JSON.stringify(user));
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data || 'Registration failed',
      };
    }
  };

  const logout = () => {
    setUser(null);
    setTokens(null);
    localStorage.removeItem('tokens');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  };

  const switchRole = async (role) => {
    try {
      const response = await api.post('/auth/switch-role/', { role });
      const { tokens: newTokens, user: nextUser } = response.data;
      setTokens(newTokens);
      setUser(nextUser);
      localStorage.setItem('tokens', JSON.stringify(newTokens));
      localStorage.setItem('user', JSON.stringify(nextUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${newTokens.access}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Could not switch role',
      };
    }
  };

  const refreshToken = async () => {
    if (!tokens?.refresh) return;
    
    try {
      const response = await api.post('/auth/token/refresh/', {
        refresh: tokens.refresh,
      });
      const newTokens = {
        ...tokens,
        access: response.data.access,
      };
      setTokens(newTokens);
      localStorage.setItem('tokens', JSON.stringify(newTokens));
      api.defaults.headers.common['Authorization'] = `Bearer ${newTokens.access}`;
    } catch (error) {
      logout();
    }
  };

  const value = {
    user,
    tokens,
    loading,
    login,
    register,
    logout,
    switchRole,
    refreshToken,
    fetchUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

